import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Trophy, Users, Loader2, ArrowLeft, Calendar, Rocket, Globe, Linkedin, Instagram } from 'lucide-react';
import { ContentSections } from '../../components/public/ContentSections';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';

export const ChallengeDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [challenge, setChallenge] = useState(null);
  const [judges, setJudges] = useState([]);
  const [startups, setStartups] = useState([]);
  const [eventInfo, setEventInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const chRes = await api.get(`/challenges/${slug}`);
        setChallenge(chRes.data);
        const jRes = await api.get(`/challenges/${chRes.data.id}/judges?only_visible=true`);
        setJudges(jRes.data || []);
        // Only load startups when challenge is published (not draft)
        if (chRes.data.status && chRes.data.status !== 'draft') {
          try {
            const sRes = await api.get(`/challenges/${chRes.data.id}/startups`);
            setStartups(sRes.data || []);
          } catch { /* no startups */ }
        }
        if (chRes.data.event_id) {
          try {
            const evRes = await api.get(`/events/${chRes.data.event_id}`);
            setEventInfo(evRes.data);
          } catch { /* no event */ }
        }
      } catch (err) { console.error(err); }
      finally {
        setLoading(false);
        if (window.location.hash) {
          const target = window.location.hash.slice(1);
          const attempt = (tries = 10) => {
            const el = document.getElementById(target);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            else if (tries > 0) setTimeout(() => attempt(tries - 1), 150);
          };
          requestAnimationFrame(() => requestAnimationFrame(() => attempt()));
        }
      }
    };
    load();
  }, [slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-primary" size={32} /></div>;
  if (!challenge) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Challenge non trovata</div>;

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso.slice(0, 10);
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' });
  };

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="challenge-detail-public-page">
      <div className="max-w-4xl mx-auto space-y-10">
        <button
          onClick={() => eventInfo ? navigate(`/events/${eventInfo.slug || eventInfo.id}`) : navigate('/challenges')}
          className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1 transition-colors"
          data-testid="back-to-challenges-btn"
        >
          <ArrowLeft size={14} /> {eventInfo ? `Torna a ${eventInfo.title}` : 'Torna alle challenge'}
        </button>

        <div className="space-y-4 text-center">
          <Trophy size={36} className="mx-auto text-primary" />
          <h1 className="font-heading text-3xl md:text-4xl font-bold">{challenge.title}</h1>
          {challenge.description && (
            <p className="text-muted-foreground max-w-2xl mx-auto leading-relaxed">{challenge.description}</p>
          )}
          {challenge.start_time && (
            <p className="text-sm text-muted-foreground inline-flex items-center gap-2 justify-center">
              <Calendar size={14} />
              {formatDate(challenge.start_time)} — {formatDate(challenge.end_time)}
            </p>
          )}
        </div>

        {/* Content blocks (if defined) */}
        {challenge.content_sections && challenge.content_sections.length > 0 && (
          <ContentSections sections={challenge.content_sections} />
        )}

        {/* Tabs: Giuria (default) + Startup in gara (only when published with startups) */}
        {(judges.length > 0 || startups.length > 0) && (
          <Tabs defaultValue="judges" className="w-full" data-testid="challenge-tabs">
            <TabsList className="grid grid-cols-2 max-w-md mx-auto mb-8 bg-white/5 border border-white/10">
              <TabsTrigger value="judges" data-testid="tab-trigger-judges">
                <Users size={16} className="mr-2" /> Giuria{judges.length > 0 ? ` (${judges.length})` : ''}
              </TabsTrigger>
              <TabsTrigger
                value="startups"
                disabled={startups.length === 0 || challenge.status === 'draft'}
                data-testid="tab-trigger-startups"
              >
                <Rocket size={16} className="mr-2" /> Startup in gara{startups.length > 0 ? ` (${startups.length})` : ''}
              </TabsTrigger>
            </TabsList>

            {/* JUDGES TAB */}
            <TabsContent value="judges" className="space-y-6">
              {judges.length === 0 ? (
                <p className="text-center text-muted-foreground py-10">Nessun giurato ancora annunciato.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
                  {[...judges]
                    .sort((a, b) => (a.first_name || '').localeCompare(b.first_name || '', 'it', { sensitivity: 'base' }))
                    .map(j => (
                    <Link
                      key={j.id}
                      to={`/challenges/judge/${j.profile_slug || j.auth_hash}`}
                      className="group glass rounded-2xl p-6 text-center hover:border-primary/50 transition-all"
                      data-testid={`judge-card-${j.id}`}
                    >
                      {j.image ? (
                        <img
                          src={j.image.startsWith('http') ? j.image : `${process.env.REACT_APP_BACKEND_URL}${j.image}`}
                          alt={`${j.first_name} ${j.last_name}`}
                          className="w-36 h-36 md:w-40 md:h-40 rounded-full object-cover mx-auto mb-4 group-hover:scale-105 transition-transform ring-2 ring-white/10 group-hover:ring-primary/40"
                        />
                      ) : (
                        <div className="w-36 h-36 md:w-40 md:h-40 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4 ring-2 ring-white/10">
                          <Users size={48} className="text-primary" />
                        </div>
                      )}
                      <p className="font-heading font-bold text-lg">{j.first_name} {j.last_name}</p>
                      {j.job_title && <p className="text-sm text-primary/90 mt-1.5 font-medium leading-snug">{j.job_title}</p>}
                    </Link>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* STARTUPS TAB — visible only when challenge is published */}
            <TabsContent value="startups" className="space-y-6" data-testid="public-startups-section">
              <div>
                {[...startups]
                  .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'it', { sensitivity: 'base' }))
                  .map((s) => (
                  <div
                    key={s.id}
                    className="glass rounded-2xl p-6 flex flex-col gap-4 hover:border-primary/40 transition-all"
                    data-testid={`public-startup-${s.id}`}
                  >
                    <div className="flex items-start gap-4">
                      {s.logo ? (
                        <img
                          src={s.logo.startsWith('http') ? s.logo : `${process.env.REACT_APP_BACKEND_URL}${s.logo}`}
                          alt={s.name}
                          className="w-16 h-16 rounded-xl object-contain bg-white p-1.5 flex-shrink-0"
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0">
                          <Rocket size={24} className="text-primary" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <h3 className="font-heading font-bold text-lg leading-tight">{s.name}</h3>
                        {(s.website || s.linkedin || s.instagram) && (
                          <div className="flex items-center gap-3 mt-2 text-muted-foreground">
                            {s.website && (
                              <a href={s.website} target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors" title="Sito web" data-testid={`startup-website-${s.id}`}>
                                <Globe size={16} />
                              </a>
                            )}
                            {s.linkedin && (
                              <a href={s.linkedin} target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors" title="LinkedIn" data-testid={`startup-linkedin-${s.id}`}>
                                <Linkedin size={16} />
                              </a>
                            )}
                            {s.instagram && (
                              <a href={s.instagram} target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors" title="Instagram" data-testid={`startup-instagram-${s.id}`}>
                                <Instagram size={16} />
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                      {s.founder_photo && (
                        <img
                          src={s.founder_photo.startsWith('http') ? s.founder_photo : `${process.env.REACT_APP_BACKEND_URL}${s.founder_photo}`}
                          alt="Founder"
                          className="w-12 h-12 rounded-full object-cover ring-2 ring-white/10 flex-shrink-0"
                          title="Founder"
                        />
                      )}
                    </div>
                    {s.description && (
                      <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">{s.description}</p>
                    )}
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
};
